import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Edit2,
  Trash2,
  Save,
  Send,
  Plus,
  AlertTriangle,
  Search,
  X,
  Loader2,
} from "lucide-react";
import MembersSettingsSkeleton from "./skeletons/MembersSettingsSkeleton";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { MEMBER_LIMITS } from "@/lib/settings/settingsLimits";
import { BookingNotificationControl } from "@/components/SettingsOptions/Members/components/BookingNotificationControl";
import { getDefaultCompanyServices } from "@/dispatchers/pharmacyServiceDispatchers";
import {
  Team,
  InviteData,
  Member,
  TimeBlock,
  MemberAvailability,
  useMembersData,
  availableQualifications,
  availableRegistrations,
  availablePharmacies,
  dayNames,
  dayLabels,
  defaultMemberTemplate,
  Titles,
} from "@/lib/settings/membersData";
import { checkUserPermissions } from "@/utils/checkUserPermissions";

import {
  accessTitanversUserUpsert,
  accessTitanversUserView,
  accessTitanverseFullAccess,
} from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// Custom scrollbar styles
const scrollbarStyles = `
  .members-scrollable::-webkit-scrollbar {
    width: 8px;
  }
  .members-scrollable::-webkit-scrollbar-track {
    background: transparent;
  }
  .members-scrollable::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
  .members-scrollable::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
  .members-scrollable.dark-mode::-webkit-scrollbar-thumb {
    background: #4b5563;
  }
  .members-scrollable.dark-mode::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = scrollbarStyles;
  document.head.appendChild(styleSheet);
}

// TypeScript interfaces for member settings
interface MembersSettingsTabProps {
  isDarkMode?: boolean;
  onInviteMember: () => void;
  onEditMember: (member: Member) => void;
  onDeleteMember: (memberId: string) => void;
  // ADD THESE:
  onResendInvite?: (userEmail: string, inviteId: string) => void;
  onDeleteInvite?: (inviteId: string) => void;
  onPendingTabOpen?: () => void;
}

interface InviteMemberPanelProps {
  teams: Team[];
  isDarkMode?: boolean;
  onInvite: (inviteData: InviteData) => void;
  onBack: () => void;
}

interface MemberFormPanelProps {
  member?: Member;
  isDarkMode?: boolean;
  onSave: (memberData: Partial<Member>) => void;
  onBack: () => void;
}

// Helper function to get the latest user pack
const getLatestUserPack = (billingData: any) => {
  if (
    !billingData?.planDetails?.UserPacks ||
    !Array.isArray(billingData.planDetails.UserPacks)
  ) {
    return null;
  }

  const userPacks = billingData?.planDetails?.UserPacks;

  // First, try to find packs with null SubscriptionEndDate (currently active)
  const activePacks = userPacks.filter(
    (pack) => pack.SubscriptionEndDate === null,
  );

  if (activePacks.length > 0) {
    // If multiple active packs, get the one with latest SubscriptionDate
    const latestActivePack = activePacks.reduce((latest, current) => {
      const latestDate = new Date(latest.SubscriptionDate);
      const currentDate = new Date(current.SubscriptionDate);
      return currentDate > latestDate ? current : latest;
    });
    return latestActivePack;
  }

  // If no active packs, get the one with latest SubscriptionDate overall
  const latestPack = userPacks.reduce((latest, current) => {
    const latestDate = new Date(latest.SubscriptionDate);
    const currentDate = new Date(current.SubscriptionDate);
    return currentDate > latestDate ? current : latest;
  });

  return latestPack;
};

// Main MembersSettingsTab component
export function MembersSettingsTab({
  onInviteMember,
  onEditMember,
  onDeleteMember,
  onResendInvite,
  onDeleteInvite,
}: MembersSettingsTabProps) {
  const {
    initialMembers,
    isMemberLoading,
    darkMode: isDarkMode,
    companySubscription,
    ifBillingEnabled,
    companySubscriptionStatus,
    generalData,
    companyId,
    pendingInvites,
  } = useMembersData();
  const [activeTab, setActiveTab] = useState<"active" | "pending">("active");
  const [searchQuery, setSearchQuery] = useState("");

  const members = initialMembers;
  const billingData = companySubscription;

  // Filter members based on search query
  const filteredMembers = members.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (member?.forename || "").toLowerCase().includes(searchLower) ||
      (member?.surname || "").toLowerCase().includes(searchLower) ||
      (member?.email || "").toLowerCase().includes(searchLower) ||
      (member?.role || "").toLowerCase().includes(searchLower)
    );
  });
  // Filter pending invites based on search query
  const filteredPendingInvites =
    pendingInvites?.filter((invite) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        (invite?.userEmail || "").toLowerCase().includes(searchLower) ||
        (invite?.role || "").toLowerCase().includes(searchLower)
      );
    }) || [];
  // Get the latest user pack
  const latestUserPack = getLatestUserPack(billingData);

  // Calculate user pack information based on latest pack
  const totalUserPacks = latestUserPack ? latestUserPack.quantity : 0;
  const usersPerPack = billingData?.planDetails?.usersPerPack || 1;
  const totalAvailableUsers = totalUserPacks * usersPerPack;
  const currentActiveUsers =
    members?.filter((member) => member?.active)?.length || 0;
  const hasAvailableSlots =
    currentActiveUsers < companySubscription?.totalUserPacks * 10;

  const hasUserPermission = checkUserPermissions(accessTitanversUserUpsert);

  if (isMemberLoading) {
    return <MembersSettingsSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Team Members
        </h2>

        <div
          onClick={() => {
            if (!hasUserPermission) {
              setShowPermissionMessage("Create or update user");
              setShowPermissionModal(true);
            }
          }}
          className={!hasUserPermission ? "cursor-not-allowed" : ""}
        >
          <Button
            size="sm"
            className={`bg-slate-900 hover:bg-slate-800 ${
              !hasUserPermission ? "opacity-50" : ""
            }`}
            onClick={() => {
              if (!hasUserPermission) return;
              onInviteMember();
            }}
            disabled={!hasUserPermission}
          >
            <Mail className="w-4 h-4 mr-2" /> Invite Member
          </Button>
        </div>
      </div>

      {/* User Pack Count System */}
      {/* {ifBillingEnabled && generalData?.companySubscriptionStatus === 2 && (
        <div
          className={`border rounded-lg p-4 transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Subscription Overview
              </h3>
              <p
                className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                User packs and team capacity
              </p>
              {latestUserPack && (
                <p
                  className={`text-xs mt-1 transition-colors duration-300 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Latest pack: {latestUserPack.quantity} user pack(s) •
                  Subscribed on{" "}
                  {new Date(
                    latestUserPack.SubscriptionDate,
                  ).toLocaleDateString()}
                  {latestUserPack.SubscriptionEndDate && (
                    <span>
                      {" "}
                      • Ends on{" "}
                      {new Date(
                        latestUserPack.SubscriptionEndDate,
                      ).toLocaleDateString()}
                    </span>
                  )}
                </p>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="text-center">
                <div
                  className={`text-2xl font-bold transition-colors duration-300 ${
                    isDarkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                >
                  {companySubscription?.totalUserPacks}
                </div>
                <div
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  User Packs
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold transition-colors duration-300 ${
                    isDarkMode ? "text-green-400" : "text-green-600"
                  }`}
                >
                  {currentActiveUsers}/
                  {companySubscription?.totalUserPacks * 10}
                </div>
                <div
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Users Used
                </div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold transition-colors duration-300 ${
                    isDarkMode ? "text-purple-400" : "text-purple-600"
                  }`}
                >
                  {companySubscription?.totalUserPacks * 10 -
                    currentActiveUsers}
                </div>
                <div
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Available
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Each user pack includes 10 users • Total capacity:{" "}
              {companySubscription?.totalUserPacks * 10} users
            </div>
            {!hasAvailableSlots && (
              <div
                className={`flex items-center gap-2 mt-3 p-3 rounded-md transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-red-900/20 border border-red-600"
                    : "bg-red-50 border border-red-200"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 flex-shrink-0 ${
                    isDarkMode ? "text-red-400" : "text-red-500"
                  }`}
                />
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-red-400" : "text-red-700"
                  }`}
                >
                  All user slots are full. You cannot invite new members until
                  you add more user packs or deactivate existing users.
                </span>
              </div>
            )}
          </div>
        </div>
      )} */}
      <Tabs
        defaultValue="active"
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "active" | "pending")}
      >
        <TabsList className="border-b w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger value="active" className="rounded-none">
            Active Members
            <Badge
              variant="secondary"
              className={`ml-2 ${
                isDarkMode
                  ? "bg-gray-700 text-gray-200"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {filteredMembers.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-none">
            Pending Invites
            <Badge
              variant="secondary"
              className={`ml-2 ${
                isDarkMode
                  ? "bg-gray-700 text-gray-200"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {filteredPendingInvites.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Search Bar */}
        <div className="mt-6 mb-4">
          <div
            className={`relative transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <Input
              placeholder={
                activeTab === "active"
                  ? "Search by name, email, or role..."
                  : "Search by email or role..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-10 transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-700"
                  : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
              }`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                  isDarkMode
                    ? "text-gray-400 hover:text-gray-300"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <TabsContent
          value="active"
          className={`mt-6 max-h-[600px] overflow-y-auto pr-4 members-scrollable ${
            isDarkMode ? "dark-mode" : ""
          }`}
        >
          <div className="space-y-4">
            {filteredMembers.length === 0 ? (
              <div
                className={`text-center py-12 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>
                  {searchQuery
                    ? "No members found matching your search"
                    : "No active members"}
                </p>
              </div>
            ) : (
              filteredMembers.map((member, index) => (
                <div
                  key={`${member.id}-${index}`}
                  className={`border rounded-lg p-4 transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                          isDarkMode ? "bg-gray-600" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-600"
                          }`}
                        >
                          {`${member.forename?.charAt(0) ??
                            ""}${member.surname?.charAt(0) ?? ""}`}
                        </span>
                      </div>
                      <div>
                        <h3
                          className={`font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {Titles?.[member.title] ?? ""} {member.forename}{" "}
                          {member.surname}
                        </h3>
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {member.email}
                        </p>
                        {member.isClinician && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            Clinician
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {member.endDate && (
                        <Badge
                          variant="outline"
                          className={`text-xs font-medium ${
                            isDarkMode
                              ? "border-amber-600 bg-amber-900/30 text-amber-300"
                              : "border-amber-400 bg-amber-50 text-amber-700"
                          }`}
                        >
                          Access ends{" "}
                          {new Date(member.endDate).toLocaleDateString("en-GB")}
                        </Badge>
                      )}
                      <Badge variant="secondary">{member.role}</Badge>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!hasUserPermission) {
                              setShowPermissionMessage("Create or update user");
                              setShowPermissionModal(true);
                              return;
                            }

                            onEditMember(member);
                          }}
                          className={
                            isDarkMode
                              ? "text-gray-300 hover:text-white hover:bg-gray-700"
                              : ""
                          }
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!hasUserPermission) {
                              setShowPermissionMessage("Delete user");
                              setShowPermissionModal(true);
                              return;
                            }

                            onDeleteMember(member.email);
                          }}
                          className={
                            isDarkMode
                              ? "text-red-400 hover:text-red-300 hover:bg-gray-700"
                              : "text-red-500 hover:text-red-600"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent
          value="pending"
          className={`mt-6 max-h-[600px] overflow-y-auto pr-4 members-scrollable ${
            isDarkMode ? "dark-mode" : ""
          }`}
        >
          <div className="space-y-4">
            {filteredPendingInvites?.length === 0 ? (
              <div
                className={`text-center py-12 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>
                  {searchQuery
                    ? "No invites found matching your search"
                    : "No pending invites"}
                </p>
              </div>
            ) : (
              filteredPendingInvites?.map((invite) => (
                <div
                  key={invite.id}
                  className={`border rounded-lg p-4 transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800/50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                          isDarkMode ? "bg-gray-600" : "bg-gray-300"
                        }`}
                      >
                        <Mail
                          className={`w-5 h-5 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        />
                      </div>
                      <div>
                        <h3
                          className={`font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {invite?.userEmail}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              isDarkMode
                                ? "border-gray-600 text-gray-400"
                                : "border-gray-300 text-gray-600"
                            }`}
                          >
                            {invite?.roleName}
                          </Badge>
                          <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Invited by {invite?.invitedBy}
                          </span>
                        </div>
                        <p
                          className={`text-xs mt-1 ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          Sent{" "}
                          {new Date(
                            invite?.invitationDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {invite.endDate && (
                        <Badge
                          variant="outline"
                          className={`text-xs font-medium ${
                            isDarkMode
                              ? "border-amber-600 bg-amber-900/30 text-amber-300"
                              : "border-amber-400 bg-amber-50 text-amber-700"
                          }`}
                        >
                          Access ends{" "}
                          {new Date(invite.endDate).toLocaleDateString("en-GB")}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (!hasUserPermission) {
                            setShowPermissionMessage("Create or update user");
                            setShowPermissionModal(true);
                            return;
                          }

                          onResendInvite?.(invite?.userEmail, invite?._id);
                        }}
                        className={
                          isDarkMode
                            ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-100"
                        }
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Resend
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (!hasUserPermission) {
                            setShowPermissionMessage("Create or update user");
                            setShowPermissionModal(true);
                            return;
                          }

                          onDeleteInvite?.(invite._id);
                        }}
                        className={
                          isDarkMode
                            ? "text-red-400 hover:bg-red-900/20"
                            : "text-red-600 hover:bg-red-50"
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
function MemberDateRangeToggle({
  isDarkMode,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: {
  isDarkMode: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (v: string) => void;
  onEndDateChange: (v: string) => void;
}) {
  const [enabled, setEnabled] = useState(() => !!(startDate || endDate));

  // Sync enabled state with props when they change
  useEffect(() => {
    setEnabled(!!(startDate || endDate));
  }, [startDate, endDate]);

  return (
    <div className="mt-4">
      <div className="flex items-center gap-3 mb-3">
        <Switch
          id="member-date-range-toggle"
          checked={enabled}
          onCheckedChange={(checked) => {
            setEnabled(checked);
            if (!checked) {
              onStartDateChange("");
              onEndDateChange("");
            }
          }}
        />
        <Label htmlFor="member-date-range-toggle" className="cursor-pointer">
          Set access start &amp; end date
        </Label>
        {!enabled && (
          <span
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            (Indefinite)
          </span>
        )}
      </div>
      {enabled && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="member-startDate">Start Date</Label>
            <Input
              id="member-startDate"
              type="date"
              value={startDate || ""}
              onChange={(e) => {
                onStartDateChange(e.target.value);
              }}
              className={
                isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="member-endDate">End Date</Label>
            <Input
              id="member-endDate"
              type="date"
              value={endDate || ""}
              onChange={(e) => onEndDateChange(e.target.value)}
              min={startDate || undefined}
              className={
                isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

// InviteMemberPanel component for inviting new members
export function InviteMemberPanel({
  onInvite,
  onBack,
}: InviteMemberPanelProps) {
  const {
    teams,
    isTeamMemberInviteLoading,
    availableRoles,
    darkMode: isDarkMode,
    formData,
    errors,
    register,
    isValid,
    isDirty,
    handleSubmit,
    handleRoleChange,
    handleToggleTeam,
    handleMessageChange,
    setError,
    setFormData,
    initialMembers,
    currentCompanyId,
    companyId,
  } = useMembersData();

  const [hasDateRange, setHasDateRange] = useState(false);

  const updateField = (field: string, value: any) => {
    setFormData((prev: InviteData) => ({ ...prev, [field]: value }));
  };

  const normalizeEmail = (value: string) => value.trim().toLowerCase();
  const isDuplicateEmail = (value: string) => {
    const normalized = normalizeEmail(value);
    const companyKey = currentCompanyId || companyId || "";
    return initialMembers.some(
      (member) =>
        member.active &&
        member.companyID === companyKey &&
        member.email.toLowerCase() === normalized,
    );
  };

  const onSubmit = () => {
    const emailValue = formData.email || "";
    if (isDuplicateEmail(emailValue)) {
      setError("email", {
        type: "manual",
        message:
          "This email is already in use. Please try another email address.",
      });
      return;
    }

    if (
      emailValue.trim() &&
      formData.selectedTeams.length > 0 &&
      formData.role
    ) {
      onInvite(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`sticky top-0 z-10 flex items-center gap-4 pb-4 border-b transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back
        </Button>
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Invite Team Member
        </h2>
      </div>

      <div className="space-y-6">
        {/* Email & Role */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Invitation Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="Enter email address"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email format",
                  },
                  validate: (value: string) => {
                    const normalized = value.trim().toLowerCase();
                    const companyKey = currentCompanyId || companyId || "";
                    const existingMember = initialMembers.find(
                      (member) =>
                        member.active &&
                        member.companyID === companyKey &&
                        member.email.toLowerCase() === normalized,
                    );
                    return existingMember
                      ? "This email is already in use. Please try another email address."
                      : true;
                  },
                })}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">
                Role <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.roleId}
                onValueChange={(id) => handleRoleChange(id)}
              >
                <SelectTrigger
                  className={errors.roleId ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role._id} value={role._id}>
                      {role.roleName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.roleId && (
                <p className="text-sm text-red-500">{errors.roleId.message}</p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-3">
              <Switch
                id="invite-date-range-toggle"
                checked={hasDateRange}
                onCheckedChange={(checked) => {
                  setHasDateRange(checked);
                  if (!checked) {
                    updateField("startDate", "");
                    updateField("endDate", "");
                  }
                }}
              />
              <Label
                htmlFor="invite-date-range-toggle"
                className="cursor-pointer"
              >
                Set access start &amp; end date
              </Label>
              {!hasDateRange && (
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  (Indefinite)
                </span>
              )}
            </div>
            {hasDateRange && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-startDate">Start Date</Label>
                  <Input
                    id="invite-startDate"
                    type="date"
                    value={formData.startDate || ""}
                    onChange={(e) => updateField("startDate", e.target.value)}
                    className={
                      isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-endDate">End Date</Label>
                  <Input
                    id="invite-endDate"
                    type="date"
                    value={formData.endDate || ""}
                    onChange={(e) => updateField("endDate", e.target.value)}
                    min={formData.startDate || undefined}
                    className={
                      isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Team Selection */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Assign to Teams
          </h3>
          <p
            className={`text-sm mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Select which teams this member will be invited to join
          </p>
          <div className="grid grid-cols-1 gap-3">
            {teams.map((team, index) => (
              <div
                key={`${team.teamID}-${index}`}
                className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  id={`team-${team.teamID}`}
                  checked={formData.selectedTeams.includes(team.teamID)}
                  onChange={() => handleToggleTeam(team.teamID)}
                  className="rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 border border-gray-300 rounded-full"
                      style={{ backgroundColor: team.teamColor }}
                    />
                    <label
                      htmlFor={`team-${team.teamID}`}
                      className={`font-medium cursor-pointer transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {team.teamName}
                    </label>
                  </div>
                  <p
                    className={`text-sm mt-1 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {team.teamDescription}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Message */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Invitation Message
          </h3>
          <div className="space-y-2">
            <Label htmlFor="invite-message">Custom Message (Optional)</Label>
            <Textarea
              id="invite-message"
              value={formData.message || ""}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="Add a personal message to the invitation..."
              rows={4}
              className={
                isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
              }
            />
          </div>
        </div>

        {/* Preview */}
        <div
          className={`border rounded-lg p-4 transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-700 border-gray-600"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <h4
            className={`font-medium mb-2 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Invitation Preview
          </h4>
          <div
            className={`text-sm space-y-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            <p>
              <span className="font-medium">To:</span>{" "}
              {formData.email || "email@example.com"}
            </p>
            <p>
              <span className="font-medium">Role:</span>{" "}
              {formData.role || "Not selected"}
            </p>
            <p>
              <span className="font-medium">Teams:</span>{" "}
              {formData.selectedTeams.length > 0
                ? teams
                    .filter((t) => formData.selectedTeams.includes(t.teamID))
                    .map((t) => t.teamName)
                    .join(", ")
                : "No teams selected"}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isValid || !isDirty || isTeamMemberInviteLoading}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Send className="w-4 h-4 mr-2" />
          {isTeamMemberInviteLoading ? "Sending... " : "Send Invitation"}
        </Button>
      </div>
    </form>
  );
}

// Helper function to check if a role has full system access permission
const roleHasFullSystemAccess = (role: any) => {
  return (
    role?.permissions &&
    Array.isArray(role.permissions) &&
    role.permissions.some(
      (perm: any) =>
        perm?.permissionName === "titanverse.admin.fullsystemaccess",
    )
  );
};

// MemberFormPanel component for adding/editing members
export function MemberFormPanel({
  member,
  onSave,
  onBack,
}: MemberFormPanelProps) {
  const [availableServices, setAvailableServices] = useState([]);
  const { companyId } = useMembersData();
  const [rolePermissionError, setRolePermissionError] = useState("");
  const [roleRequiredError, setRoleRequiredError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const services = await getDefaultCompanyServices(companyId);
        const activeServices = (services || []).filter(
          (service) => service.isActive === true,
        );
        setAvailableServices(activeServices);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, [member]);

  const getDefaultNotifications = () => ({
    taskAssigned: false,
    taskUpdated: false,
    labResults: false,
    bookingMade: { enabled: true, services: {} },
    bookingRescheduledOrCancelled: { enabled: true, services: {} },
  });

  const [formData, setFormData] = useState(() => {
    const formatDateForInput = (dateString: string | undefined | null) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch {
        return "";
      }
    };
    if (member) {
      // Format dates for input fields
      const formattedStartDate = formatDateForInput(member.startDate);
      const formattedEndDate = formatDateForInput(member.endDate);

      return {
        ...defaultMemberTemplate,
        ...member,
        qualifications: member.qualifications || [],
        registrations: member.registrations || [],
        servicesProvided: member.servicesProvided || [],
        pharmacies: member.pharmacies || [],
        notifications: member.notifications ?? getDefaultNotifications(),
        availability: {
          ...defaultMemberTemplate.availability,
          ...(member.availability || {}),
        },
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };
    }
    return {
      ...defaultMemberTemplate,
      notifications: getDefaultNotifications(),
      startDate: "", // Add this
      endDate: "", // Add this
    };
  });

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (section: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateAvailability = (
    day: keyof MemberAvailability,
    field: string,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], [field]: value },
      },
    }));
  };

  const addTimeBlock = (day: keyof MemberAvailability) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: [
            ...prev.availability[day].timeBlocks,
            { from: "09:00", to: "17:00" },
          ],
        },
      },
    }));
  };

  const removeTimeBlock = (
    day: keyof MemberAvailability,
    blockIndex: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: prev.availability[day].timeBlocks.filter(
            (_, index) => index !== blockIndex,
          ),
        },
      },
    }));
  };

  const updateTimeBlock = (
    day: keyof MemberAvailability,
    blockIndex: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: prev.availability[day].timeBlocks.map((block, index) =>
            index === blockIndex ? { ...block, [field]: value } : block,
          ),
        },
      },
    }));
  };
  const handleMultiSelect = (
    field: string,
    value: string,
    isSelected: boolean,
  ) => {
    setFormData((prev) => {
      let current: string[] = [];

      if (Array.isArray(prev[field])) {
        current = prev[field] as string[];
      } else if (typeof prev[field] === "string" && prev[field].trim() !== "") {
        current = prev[field].split(",").map((v) => v.trim());
      }

      if (isSelected) {
        return {
          ...prev,
          [field]: current.filter((item) => item !== value),
        };
      }

      if (!current.includes(value)) {
        return {
          ...prev,
          [field]: [...current, value],
        };
      }

      return prev;
    });
  };
  const {
    availableRoles,
    defaultPharmacyServices,
    allPharmacies,
    darkMode: isDarkMode,
  } = useMembersData();

  useEffect(() => {
    if (
      availableRoles.length > 0 &&
      formData.roleId &&
      !availableRoles.some((r) => r._id === formData.roleId)
    ) {
      updateField("roleId", "");
      updateField("role", "");
    }
  }, [availableRoles]);

  const isRoleValid =
    !!formData.roleId && availableRoles.some((r) => r._id === formData.roleId);
  const isFormInvalid =
    !formData.forename.trim() ||
    !formData.surname.trim() ||
    !formData.email.trim() ||
    !isRoleValid;
  const hasUserPermission = checkUserPermissions(accessTitanversUserUpsert);

  const handleSubmit = async () => {
    if (!isRoleValid) {
      setRoleRequiredError(true);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`sticky top-0 z-10 flex items-center gap-4 pb-4 border-b transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back
        </Button>
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {member ? "Edit Member" : "Add New Member"}
        </h2>
      </div>

      <div className="space-y-8">
        {/* General */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            General
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Select
                value={formData.title}
                onValueChange={(value) => updateField("title", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None</SelectItem>
                  <SelectItem value="1">Mr.</SelectItem>
                  <SelectItem value="2">Mrs.</SelectItem>
                  <SelectItem value="3">Miss</SelectItem>
                  <SelectItem value="4">Ms.</SelectItem>
                  <SelectItem value="5">Dr.</SelectItem>
                  <SelectItem value="6">Prof.</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="forename">
                Forename <span className="text-red-500">*</span>
              </Label>
              <Input
                id="forename"
                value={formData.forename}
                onChange={(e) => updateField("forename", e.target.value)}
                placeholder="Enter forename"
                maxLength={MEMBER_LIMITS.forename}
              />
              <CharLimitMessage value={formData.forename} max={MEMBER_LIMITS.forename} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="surname">
                Surname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="surname"
                value={formData.surname}
                onChange={(e) => updateField("surname", e.target.value)}
                placeholder="Enter surname"
                maxLength={MEMBER_LIMITS.surname}
              />
              <CharLimitMessage value={formData.surname} max={MEMBER_LIMITS.surname} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                disabled={!!member}
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="Enter email address"
                maxLength={MEMBER_LIMITS.email}
              />
              <CharLimitMessage value={formData.email} max={MEMBER_LIMITS.email} />
            </div>
            <div className="flex items-center mt-8 space-x-2">
              <Switch
                id="is-clinician"
                checked={formData.isClinician}
                onCheckedChange={(checked) =>
                  updateField("isClinician", checked)
                }
              />
              <Label htmlFor="is-clinician">Is Clinician</Label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Qualifications</Label>
              <Select
                value=""
                onValueChange={(value) =>
                  handleMultiSelect("qualifications", value, false)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select qualifications" />
                </SelectTrigger>
                <SelectContent>
                  {availableQualifications.map((qual) => (
                    <SelectItem key={qual} value={qual}>
                      {qual}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.qualifications && formData.qualifications.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.qualifications.map((qual) => (
                    <Badge key={qual} variant="secondary" className="text-xs">
                      {qual}
                      <button
                        onClick={() =>
                          handleMultiSelect("qualifications", qual, true)
                        }
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Registrations</Label>
              <Select
                value=""
                onValueChange={(value) =>
                  handleMultiSelect("registrations", value, false)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select registrations" />
                </SelectTrigger>
                <SelectContent>
                  {availableRegistrations.map((reg) => (
                    <SelectItem key={reg} value={reg}>
                      {reg}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.registrations && formData.registrations.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.registrations.map((reg) => (
                    <Badge key={reg} variant="secondary" className="text-xs">
                      {reg}
                      <button
                        onClick={() =>
                          handleMultiSelect("registrations", reg, true)
                        }
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User Access */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            User Access
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">
                Role <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.roleId}
                onValueChange={(id) => {
                  const selectedRole = availableRoles.find((r) => r._id === id);
                  const hasFullAccess = roleHasFullSystemAccess(selectedRole);
                  const userHasFullAccess = checkUserPermissions(
                    accessTitanverseFullAccess,
                  );

                  // Check if selected role has full system access
                  if (hasFullAccess) {
                    // Check if current user has full system access permission
                    if (!userHasFullAccess) {
                      setRolePermissionError(
                        "You don't have permission to assign this role. Only users with full system access can assign roles with full system access.",
                      );
                      return;
                    }
                  }

                  setRolePermissionError("");
                  setRoleRequiredError(false);
                  updateField("role", selectedRole?.roleName);
                  updateField("roleId", id);
                }}
              >
                <SelectTrigger
                  className={
                    roleRequiredError && !isRoleValid ? "border-red-500" : ""
                  }
                >
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => {
                    const hasFullAccess = roleHasFullSystemAccess(role);
                    const userHasFullAccess = checkUserPermissions(
                      accessTitanverseFullAccess,
                    );
                    const isDisabled = hasFullAccess && !userHasFullAccess;

                    return (
                      <SelectItem
                        key={role._id}
                        value={role._id}
                        disabled={isDisabled}
                      >
                        <div className="flex items-center gap-2">
                          {role.roleName}
                          {hasFullAccess && !userHasFullAccess && (
                            <AlertTriangle className="w-3 h-3 text-orange-500" />
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {rolePermissionError && (
                <div className="flex items-center gap-2 p-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {rolePermissionError}
                  </p>
                </div>
              )}
              {roleRequiredError && !isRoleValid && (
                <p className="text-sm text-red-500">Role is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Services Provided</Label>
              <Select
                value=""
                onValueChange={(value) =>
                  handleMultiSelect("servicesProvided", value, false)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select services" />
                </SelectTrigger>
                <SelectContent>
                  {defaultPharmacyServices
                    .filter(
                      (service) =>
                        service.serviceType !== 1 && service.serviceType !== 3,
                    )
                    .map((service) => (
                      <SelectItem key={service._id} value={service._id}>
                        {service.serviceName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {formData.servicesProvided &&
                formData.servicesProvided.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.servicesProvided.map((serviceId) => {
                      const serviceObj = defaultPharmacyServices.find(
                        (s) => s._id === serviceId,
                      );
                      return (
                        <Badge
                          key={serviceId}
                          variant="secondary"
                          className="text-xs"
                        >
                          {serviceObj ? serviceObj.serviceName : serviceId}
                          <button
                            onClick={() =>
                              handleMultiSelect(
                                "servicesProvided",
                                serviceId,
                                true,
                              )
                            }
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
            </div>
            <MemberDateRangeToggle
              isDarkMode={isDarkMode}
              startDate={formData.startDate || ""}
              endDate={formData.endDate || ""}
              onStartDateChange={(v) => updateField("startDate", v)}
              onEndDateChange={(v) => updateField("endDate", v)}
            />
          </div>
        </div>

        {/* Pharmacies */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Pharmacies
          </h3>
          <div className="space-y-2">
            <Label>Multi select list of pharmacies this user can work at</Label>
            <div className="grid grid-cols-2 gap-2">
              {allPharmacies.map((pharmacy) => (
                <div
                  key={pharmacy?.pharmacyName}
                  className={`flex items-center space-x-2 p-2 border rounded transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-700"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`pharmacy-${pharmacy?.pharmacyID}`}
                    checked={formData.pharmacies.includes(pharmacy?.pharmacyID)}
                    onChange={(e) =>
                      handleMultiSelect(
                        "pharmacies",
                        pharmacy?.pharmacyID,
                        !e.target.checked,
                      )
                    }
                    className="rounded"
                  />
                  <label
                    htmlFor={`pharmacy-${pharmacy}`}
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {pharmacy?.pharmacyName}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Notification Settings
          </h3>
          <div className="space-y-4">
            {/* Booking Made Notifications */}
            <BookingNotificationControl
              label="Receive notification when booking has been made"
              value={formData.notifications.bookingMade}
              isDarkMode={isDarkMode}
              availableServices={availableServices}
              onChange={(value) =>
                updateNestedField("notifications", "bookingMade", value)
              }
            />

            {/* Booking Rescheduled/Cancelled Notifications */}
            <BookingNotificationControl
              label="Receive notification when booking has been rescheduled or cancelled"
              value={formData.notifications.bookingRescheduledOrCancelled}
              isDarkMode={isDarkMode}
              availableServices={availableServices}
              onChange={(value) =>
                updateNestedField(
                  "notifications",
                  "bookingRescheduledOrCancelled",
                  value,
                )
              }
            />
          </div>
        </div>

        {/* Availability */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Availability
          </h3>
          <div className="grid grid-cols-1 gap-3">
            {dayNames.map((day, index) => (
              <div
                key={day}
                className={`border rounded p-3 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Switch
                    checked={formData.availability[day].enabled}
                    onCheckedChange={(checked) =>
                      updateAvailability(day, "enabled", checked)
                    }
                  />
                  <Label className="font-medium text-sm min-w-[80px]">
                    {dayLabels[index]}
                  </Label>

                  {formData.availability[day].enabled && (
                    <div className="flex flex-wrap items-center flex-1 gap-2">
                      {formData.availability[day].timeBlocks.map(
                        (block, blockIndex) => (
                          <div
                            key={blockIndex}
                            className="flex items-center gap-1"
                          >
                            <Input
                              type="time"
                              value={block.from}
                              onChange={(e) =>
                                updateTimeBlock(
                                  day,
                                  blockIndex,
                                  "from",
                                  e.target.value,
                                )
                              }
                              className={`w-20 h-8 text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 border-gray-500 text-white"
                                  : ""
                              }`}
                            />
                            <span
                              className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              }`}
                            >
                              -
                            </span>
                            <Input
                              type="time"
                              value={block.to}
                              onChange={(e) =>
                                updateTimeBlock(
                                  day,
                                  blockIndex,
                                  "to",
                                  e.target.value,
                                )
                              }
                              className={`w-20 h-8 text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 border-gray-500 text-white"
                                  : ""
                              }`}
                            />
                            {formData.availability[day].timeBlocks.length >
                              1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTimeBlock(day, blockIndex)}
                                className="w-6 h-6 p-0"
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            )}
                          </div>
                        ),
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addTimeBlock(day)}
                        className="h-6 px-2 text-xs"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <div
          onClick={() => {
            if (!hasUserPermission) {
              setShowPermissionMessage("Create or update user");
              setShowPermissionModal(true);
            }
          }}
          className={!hasUserPermission ? "cursor-not-allowed" : ""}
        >
          <Button
            onClick={() => {
              if (!hasUserPermission) return;
              handleSubmit();
            }}
            disabled={isFormInvalid || !hasUserPermission || isSaving}
            className={`bg-slate-900 hover:bg-slate-800 ${
              !hasUserPermission ? "opacity-50" : ""
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isSaving
              ? member
                ? "Updating..."
                : "Saving..."
              : member
              ? "Update Member"
              : "Save Member"}
          </Button>
        </div>
      </div>
    </div>
  );
}
